<script setup>
import { computed, watch, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import ConnectInboxDialog from 'dashboard/components-next/captain/pageComponents/inbox/ConnectInboxDialog.vue';
import InboxCard from 'dashboard/components-next/captain/assistant/InboxCard.vue';
import InboxPageEmptyState from 'dashboard/components-next/captain/pageComponents/emptyStates/InboxPageEmptyState.vue';

const store = useStore();
const dialogType = ref('');
const route = useRoute();
const { t } = useI18n();

const assistantId = computed(() => route.params.assistantId);
const assistantUiFlags = useMapGetter('captainAssistants/getUIFlags');
const uiFlags = useMapGetter('captainInboxes/getUIFlags');
const inboxUiFlags = useMapGetter('inboxes/getUIFlags');
const isFetchingAssistant = computed(() => assistantUiFlags.value.fetchingItem);
// The account's inboxes decide the empty state, so their fetch has to count as
// fetching too - otherwise a cold load straight to this URL shows "connect your
// first inbox" until the list lands.
const isFetching = computed(
  () => uiFlags.value.fetchingList || inboxUiFlags.value.isFetching
);

const captainInboxes = useMapGetter('captainInboxes/getRecords');
const accountInboxes = useMapGetter('inboxes/getInboxes');
const assistants = useMapGetter('captainAssistants/getRecords');

const connectedByInboxId = computed(() =>
  Object.fromEntries(captainInboxes.value.map(inbox => [inbox.id, inbox]))
);

// Which other assistant owns each inbox. An inbox belongs to at most one
// assistant, so a flat map is enough; the current assistant is left out because
// its own bindings are read off `captainInboxes`.
const ownerByInboxId = computed(() => {
  const currentId = Number(assistantId.value);
  const owners = {};

  assistants.value.forEach(assistant => {
    if (assistant.id === currentId) return;
    (assistant.inbox_ids || []).forEach(inboxId => {
      owners[inboxId] = assistant.name;
    });
  });

  return owners;
});

// Every inbox in the account, each carrying the state its switch should show:
// on only when this assistant is connected and not paused.
const channelRows = computed(() =>
  accountInboxes.value.map(inbox => ({
    inbox: {
      ...inbox,
      captain_enabled: Boolean(
        connectedByInboxId.value[inbox.id]?.captain_enabled
      ),
    },
    ownerName: ownerByInboxId.value[inbox.id] || '',
  }))
);

const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);

const handleDelete = () => {
  disconnectInboxDialog.value.dialogRef.open();
};

const connectInboxDialog = ref(null);

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value.dialogRef.open());
};
const handleAction = ({ action, id }) => {
  selectedInbox.value = captainInboxes.value.find(inbox => id === inbox.id);
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
  });
};

// Pausing is deliberately not disconnecting: the connection row survives, so the
// inbox stays in this list and only stops being answered.
const handleToggle = async ({ id, enabled }) => {
  try {
    await store.dispatch('captainInboxes/update', {
      assistantId: assistantId.value,
      inboxId: id,
      enabled,
    });
    useAlert(
      enabled
        ? t('CAPTAIN.INBOXES.TOGGLE.ENABLED_MESSAGE')
        : t('CAPTAIN.INBOXES.TOGGLE.DISABLED_MESSAGE')
    );
  } catch (error) {
    useAlert(t('CAPTAIN.INBOXES.TOGGLE.ERROR_MESSAGE'));
  }
};

// The full-channel list turns one switch into both actions: an inbox this
// assistant does not own yet is connected and enabled in a single call, while a
// connected one only resumes. Turning a switch off always pauses, never
// disconnects.
const handleChannelToggle = async ({ id, enabled }) => {
  if (!enabled || connectedByInboxId.value[id]) {
    await handleToggle({ id, enabled });
    return;
  }

  try {
    const { payload } = await store.dispatch('captainInboxes/create', {
      assistantId: assistantId.value,
      inboxIds: [id],
    });
    // The server refuses an inbox another assistant claimed since this page
    // loaded, and says so rather than raising.
    useAlert(
      payload.length
        ? t('CAPTAIN.INBOXES.TOGGLE.CONNECTED_MESSAGE')
        : t('CAPTAIN.INBOXES.CREATE.NONE_CONNECTED_MESSAGE')
    );
  } catch (error) {
    useAlert(t('CAPTAIN.INBOXES.TOGGLE.ERROR_MESSAGE'));
  }
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedInbox.value = null;
};

watch(
  assistantId,
  newId => {
    store.dispatch('captainInboxes/get', {
      assistantId: newId,
    });
    // The connect form labels inboxes that another assistant already owns, and
    // reads those bindings off the assistant list.
    store.dispatch('captainAssistants/get');
    // The shell fetches these on mount as well, but this page can be loaded
    // straight from its URL and must not race that.
    store.dispatch('inboxes/get');
  },
  { immediate: true }
);
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.INBOXES.HEADER')"
    :button-label="$t('CAPTAIN.INBOXES.ADD_NEW')"
    :button-policy="['administrator']"
    :is-fetching="isFetchingAssistant || isFetching"
    :is-empty="!accountInboxes.length"
    :show-pagination-footer="false"
    :show-know-more="false"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    @click="handleCreate"
  >
    <template #emptyState>
      <InboxPageEmptyState @click="handleCreate" />
    </template>

    <template #body>
      <div class="flex flex-col gap-8">
        <div v-if="captainInboxes.length" class="flex flex-col gap-4">
          <h2 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('CAPTAIN.INBOXES.SECTIONS.CONNECTED') }}
          </h2>
          <InboxCard
            v-for="captainInbox in captainInboxes"
            :id="captainInbox.id"
            :key="captainInbox.id"
            :inbox="captainInbox"
            @action="handleAction"
            @toggle="handleToggle"
          />
        </div>

        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <h2 class="mb-0 text-sm font-medium text-n-slate-12">
              {{ $t('CAPTAIN.INBOXES.SECTIONS.ALL_CHANNELS') }}
            </h2>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t('CAPTAIN.INBOXES.SECTIONS.ALL_CHANNELS_DESCRIPTION') }}
            </p>
          </div>
          <InboxCard
            v-for="row in channelRows"
            :id="row.inbox.id"
            :key="`channel-${row.inbox.id}`"
            :inbox="row.inbox"
            :owner-name="row.ownerName"
            :show-actions="false"
            @toggle="handleChannelToggle"
          />
        </div>
      </div>
    </template>

    <DeleteDialog
      v-if="selectedInbox"
      ref="disconnectInboxDialog"
      :entity="selectedInbox"
      :delete-payload="{
        assistantId: assistantId,
        inboxId: selectedInbox.id,
      }"
      type="Inboxes"
    />

    <ConnectInboxDialog
      v-if="dialogType"
      ref="connectInboxDialog"
      :assistant-id="assistantId"
      :type="dialogType"
      @close="handleCreateClose"
    />
  </PageLayout>
</template>
